import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  Star,
  StarBorder,
  TrendingUp,
  TrendingDown,
  ThumbUp,
  ThumbDown,
  Mood,
  MoodBad,
  SentimentSatisfied
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface CSATAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface CSATData {
  average_rating: number;
  total_responses: number;
  rating_distribution: Record<string, number>;
  recent_feedback: Array<{
    rating: number;
    feedback: string;
    date: string;
    conversation_id: number;
  }>;
}

const StarRating: React.FC<{ rating: number; size?: 'small' | 'medium' | 'large' }> = ({ 
  rating, 
  size = 'medium' 
}) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      i <= rating ? (
        <Star key={i} sx={{ color: 'warning.main', fontSize: size === 'small' ? 16 : size === 'large' ? 32 : 24 }} />
      ) : (
        <StarBorder key={i} sx={{ color: 'warning.main', fontSize: size === 'small' ? 16 : size === 'large' ? 32 : 24 }} />
      )
    );
  }
  return <Box sx={{ display: 'flex', alignItems: 'center' }}>{stars}</Box>;
};

const RatingBar: React.FC<{ rating: number; count: number; total: number }> = ({ 
  rating, 
  count, 
  total 
}) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 60, mr: 2 }}>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {rating}
        </Typography>
        <Star sx={{ color: 'warning.main', fontSize: 16 }} />
      </Box>
      <Box sx={{ flex: 1, mr: 2 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: rating >= 4 ? 'success.main' : rating >= 3 ? 'warning.main' : 'error.main'
            }
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>
        {count}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right', ml: 1 }}>
        ({percentage.toFixed(0)}%)
      </Typography>
    </Box>
  );
};

const SatisfactionMeter: React.FC<{ score: number }> = ({ score }) => {
  const getSatisfactionLevel = (score: number) => {
    if (score >= 4.5) return { label: 'Excellent', color: 'success.main', icon: <Mood /> };
    if (score >= 4.0) return { label: 'Very Good', color: 'success.light', icon: <SentimentSatisfied /> };
    if (score >= 3.0) return { label: 'Good', color: 'warning.main', icon: <SentimentSatisfied /> };
    if (score >= 2.0) return { label: 'Fair', color: 'warning.light', icon: <MoodBad /> };
    return { label: 'Poor', color: 'error.main', icon: <MoodBad /> };
  };

  const satisfaction = getSatisfactionLevel(score);
  const percentage = (score / 5) * 100;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={120}
          thickness={8}
          sx={{
            color: satisfaction.color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ color: satisfaction.color }}>
            {satisfaction.icon}
          </Box>
          <Typography variant="h5" component="div" color={satisfaction.color}>
            {score.toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <Typography variant="h6" color={satisfaction.color}>
        {satisfaction.label}
      </Typography>
    </Box>
  );
};

export const CSATAnalytics: React.FC<CSATAnalyticsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [csatData, setCSATData] = useState<CSATData | null>(null);

  useEffect(() => {
    fetchCSATData();
  }, [dateRange]);

  const fetchCSATData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getCSATReport(params);
      setCSATData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch CSAT analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!csatData || csatData.total_responses === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Star sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Customer Satisfaction Analytics
          </Typography>
        </Box>
        <Alert severity="info">
          No customer satisfaction data available for the selected period.
          Encourage customers to rate their experience to see analytics here.
        </Alert>
      </Box>
    );
  }

  // Calculate satisfaction metrics
  const positiveRatings = (csatData.rating_distribution['4_star'] || 0) + (csatData.rating_distribution['5_star'] || 0);
  const negativeRatings = (csatData.rating_distribution['1_star'] || 0) + (csatData.rating_distribution['2_star'] || 0);
  const neutralRatings = csatData.rating_distribution['3_star'] || 0;
  
  const positivePercentage = csatData.total_responses > 0 ? (positiveRatings / csatData.total_responses) * 100 : 0;
  const negativePercentage = csatData.total_responses > 0 ? (negativeRatings / csatData.total_responses) * 100 : 0;

  // Prepare data for export
  const exportData = [
    {
      average_rating: csatData.average_rating,
      total_responses: csatData.total_responses,
      positive_percentage: positivePercentage,
      negative_percentage: negativePercentage
    },
    ...(csatData.recent_feedback || [])
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Star sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Customer Satisfaction Analytics
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="csat_analytics"
          metadata={{ dateRange }}
        />
      </Box>

      {/* Overall Satisfaction Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom textAlign="center">
                Overall Satisfaction
              </Typography>
              <SatisfactionMeter score={csatData.average_rating} />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ThumbUp sx={{ color: 'success.main', mr: 1 }} />
                    <Typography variant="h6" color="success.main">
                      Positive Feedback
                    </Typography>
                  </Box>
                  <Typography variant="h3" color="success.main">
                    {positivePercentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {positiveRatings} out of {csatData.total_responses} responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ThumbDown sx={{ color: 'error.main', mr: 1 }} />
                    <Typography variant="h6" color="error.main">
                      Negative Feedback
                    </Typography>
                  </Box>
                  <Typography variant="h3" color="error.main">
                    {negativePercentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {negativeRatings} out of {csatData.total_responses} responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Responses
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {csatData.total_responses.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer ratings received
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Average Rating
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h3" color="warning.main" sx={{ mr: 1 }}>
                      {csatData.average_rating.toFixed(1)}
                    </Typography>
                    <Star sx={{ color: 'warning.main', fontSize: 32 }} />
                  </Box>
                  <StarRating rating={Math.round(csatData.average_rating)} size="small" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Rating Distribution */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rating Distribution
          </Typography>
          <Box sx={{ mt: 2 }}>
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = csatData.rating_distribution[`${rating}_star`] || 0;
              return (
                <RatingBar
                  key={rating}
                  rating={rating}
                  count={count}
                  total={csatData.total_responses}
                />
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Recent Feedback */}
      {csatData.recent_feedback.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Customer Feedback
            </Typography>
            <Box sx={{ mt: 2 }}>
              {csatData.recent_feedback.map((feedback, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: feedback.rating >= 4 ? 'success.light' : 
                             feedback.rating >= 3 ? 'warning.light' : 'error.light',
                    opacity: 0.9
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <StarRating rating={feedback.rating} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        Conversation #{feedback.conversation_id}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(feedback.date)}
                    </Typography>
                  </Box>
                  
                  {feedback.feedback && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{feedback.feedback}"
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};